'use client';

import { FC, PropsWithChildren, useEffect } from 'react';
import { ROOT_PORTALS } from 'constants/targets';
import { Portal, Drawer } from 'components/pcc-components';
import { useMyEquipmentDrawer } from '../my-equipment-drawer-context';
import { FormProvider, useForm } from 'react-hook-form';
import { useFormState } from 'react-dom';
import {
  EditEquipmentDataForm,
  EditEquipmentState
} from 'services/equipment/types';
import {
  FORM_FIELD_NICK_NAME,
  FORM_FIELD_SERIAL,
  GENERIC_ERROR
} from '../constants';
import { UNKNOWN_ERROR_MESSAGE } from 'constants/logging';
import { useFeedback } from 'providers/feedback-provider';
import { capitalizeText } from '@cat-ecom/common/utils/stringUtils';
import { useTranslations } from 'next-intl';
import { useQuickToast } from 'hooks/use-toast';
import { RegisterSignInButtonToast } from '../equipment-toast/register-signin-button-toast';
import { SelectedEquipmentInfo } from 'app/[lang]/[esite]/(core)/_header/components/equipment-widget/types';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  FORM_SAVE_EQUIPMENT,
  SAVE_EQUIPMENT,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';
import { useSuggestedPrefixes } from './suggested-prefixes-context';
import { useCoreContext } from 'providers/core-provider';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { checkIsAlpPage, getAlpSeoUrl } from '../utils';

type EditEquipmentDrawerDefaultProps = {
  currentEquipment: SelectedEquipmentInfo;
  isLoggedIn: boolean;
};
export const EditEquipmentDrawerWrapper: FC<
  PropsWithChildren<EditEquipmentDrawerDefaultProps>
> = ({ currentEquipment, children, isLoggedIn }) => {
  const { assetId, serialNumber, uniqueIndex, assetName } = currentEquipment;
  const nickName = assetId ?? assetName;
  const { updateFeedback, showFeedback, feedbackMessage } = useFeedback();
  const {
    editEquipmentDrawer,
    closeAllDrawers,
    isLoading: isDrawerLoading,
    setIsLoading: setIsDrawerLoading
  } = useMyEquipmentDrawer();
  const t = useTranslations();
  const { editEquipment } = useCoreContext();
  const { getEquipmentDetails } = useSuggestedPrefixes();
  const toast = useQuickToast();
  const pathname = usePathname();
  const params = useParams();
  const { isAlpSubpage, isAlp } = checkIsAlpPage(params, pathname);
  const router = useRouter();

  const editEquipmentDrawerProps = {
    ...editEquipmentDrawer.drawerProps,
    disableBackdropClick: editEquipmentDrawer.isChild,
    hideCloseButton: editEquipmentDrawer.isChild
  };
  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: {
      serial: serialNumber,
      nickName: nickName
    }
  });
  const {
    getValues,
    handleSubmit,
    formState: { isDirty, isValid, errors }
  } = formMethods;
  const showEditEquipmentToast = () => {
    const { model = '', productFamily = '' } = currentEquipment || {};
    const currentlyShoppingText = t('EDIT_EQUIP_SUCCESSFUL', {
      0: model,
      1: capitalizeText(productFamily)
    });

    const contentGuestToast = !isLoggedIn ? (
      <RegisterSignInButtonToast
        signInText={t('SIGN_IN')}
        registerText={t('SL_REGISTER')}
        guestSaveText={t('GUEST_EQUIPMENT_SAVE')}
        signInButtonVariant="ghost"
      />
    ) : null;

    editEquipmentDrawer.isChild
      ? closeAllDrawers()
      : editEquipmentDrawer.closeDrawer();

    toast.success({
      message: currentlyShoppingText,
      content: contentGuestToast,
      delay: 5
    });
  };
  const [state, formAction] = useFormState<
    EditEquipmentState,
    EditEquipmentDataForm
  >(editEquipment, null);
  const { fireEvent } = useFormSubmitEvent();
  const fireFormSubmissionGAEvents = (
    payload1: AnalyticsEventPayload,
    payload2?: AnalyticsEventPayload
  ) => {
    fireEvent(payload1);
    fireEvent(payload2 ?? '');
  };

  const editEquipmentWithRHFProps = () =>
    formAction.bind(null, {
      id: currentEquipment.unvalidatedItemListId,
      serialNumber: getValues(FORM_FIELD_SERIAL),
      assetName: getValues(FORM_FIELD_NICK_NAME),
      model: currentEquipment.model,
      equipmentId: currentEquipment?.productId ?? currentEquipment?.id,
      uniqueIndex
    })();

  useEffect(() => {
    if (editEquipmentDrawer.isDrawerOpen) {
      setIsDrawerLoading(false);
      formMethods.reset({
        serial: serialNumber,
        nickName
      });
      getEquipmentDetails(currentEquipment.partNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editEquipmentDrawer.isDrawerOpen]);

  useEffect(() => {
    if (!state) return;
    if (state.status === 'success') {
      showEditEquipmentToast();
      closeAllDrawers();
      fireFormSubmissionGAEvents({
        formName: SAVE_EQUIPMENT,
        formStatus: STATUS_SUCCESS,
        formContent: serialNumber,
        formContent2: `${currentEquipment.model} ${currentEquipment?.productFamily}`
      });
      if (isAlpSubpage || isAlp) {
        const redirectUrl = getAlpSeoUrl(
          currentEquipment.seoUrl,
          pathname,
          params,
          getValues(FORM_FIELD_SERIAL)
        );
        router.push(redirectUrl);
      }
    } else if (state.status === 'error' && state.data.type === GENERIC_ERROR) {
      updateFeedback(UNKNOWN_ERROR_MESSAGE);
      fireFormSubmissionGAEvents(
        {
          formName: FORM_SAVE_EQUIPMENT,
          formStatus: STATUS_FAIL,
          formFieldCausingError: 'Network Error',
          formContent: serialNumber,
          formContent2: `${currentEquipment.model} ${currentEquipment?.productFamily}`
        },
        {
          formName: SAVE_EQUIPMENT,
          formStatus: STATUS_FAIL,
          formContent: serialNumber,
          formContent2: `${currentEquipment.model} ${currentEquipment?.productFamily}`
        }
      );
    }
    setIsDrawerLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const isErrorEquipmentBySerial = Boolean(errors[FORM_FIELD_SERIAL]);
  const isSerialLengthValid =
    getValues(FORM_FIELD_SERIAL)?.length > 0 &&
    getValues(FORM_FIELD_SERIAL)?.length < 8;
  const isSubmitDisabled =
    isDrawerLoading ||
    isSerialLengthValid ||
    isErrorEquipmentBySerial ||
    !isValid ||
    !isDirty ||
    (showFeedback && feedbackMessage === GENERIC_ERROR);

  const onSubmitHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitDisabled) return;
    setIsDrawerLoading(true);
    handleSubmit(editEquipmentWithRHFProps());
  };

  return (
    <FormProvider {...formMethods}>
      <Portal id={ROOT_PORTALS}>
        <form onSubmit={onSubmitHandler} data-testid="EditEquipmentDrawer">
          <Drawer {...editEquipmentDrawerProps}>{children}</Drawer>
        </form>
      </Portal>
    </FormProvider>
  );
};
